import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { AnnouncementResponse } from "./contracts/AnnouncementResponse";
import { MessageTemplateResponse } from "./contracts/MessageTemplateResponse";
import {
  createMessageTemplate,
  deleteMessageTemplate,
  fetchAnnouncements,
  fetchMessageTemplates,
  sendAnnouncement,
  updateMessageTemplate,
} from "./slice-extensions/announcements-slice-extension";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface AnnouncementsState {
  items: AnnouncementResponse[];
  totalCount: number;
  page: number;
  totalPages: number;
  listStatus: RequestStatus;
  sendStatus: RequestStatus;
  sendError: string | null;
  templates: MessageTemplateResponse[];
  templatesStatus: RequestStatus;
}

const initialState: AnnouncementsState = {
  items: [],
  totalCount: 0,
  page: 1,
  totalPages: 0,
  listStatus: "idle",
  sendStatus: "idle",
  sendError: null,
  templates: [],
  templatesStatus: "idle",
};

const announcementsSlice = createSlice({
  name: "announcements",
  initialState,
  reducers: {
    resetSendStatus: (state) => {
      state.sendStatus = "idle";
      state.sendError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnnouncements.pending, (state) => {
        state.listStatus = "loading";
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.listStatus = "succeeded";
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchAnnouncements.rejected, (state) => {
        state.listStatus = "failed";
      })
      .addCase(sendAnnouncement.pending, (state) => {
        state.sendStatus = "loading";
        state.sendError = null;
      })
      .addCase(sendAnnouncement.fulfilled, (state, action) => {
        state.sendStatus = "succeeded";
        // The new announcement leads the history without waiting for a refetch.
        state.items = [action.payload, ...state.items];
        state.totalCount += 1;
      })
      .addCase(sendAnnouncement.rejected, (state, action) => {
        state.sendStatus = "failed";
        state.sendError = action.payload?.message ?? "Send failed";
      })
      .addCase(fetchMessageTemplates.pending, (state) => {
        state.templatesStatus = "loading";
      })
      .addCase(fetchMessageTemplates.fulfilled, (state, action) => {
        state.templatesStatus = "succeeded";
        state.templates = action.payload;
      })
      .addCase(fetchMessageTemplates.rejected, (state) => {
        state.templatesStatus = "failed";
      })
      .addCase(createMessageTemplate.fulfilled, (state, action) => {
        state.templates = [...state.templates, action.payload].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      })
      .addCase(updateMessageTemplate.fulfilled, (state, action) => {
        state.templates = state.templates
          .map((template) => (template.id === action.payload.id ? action.payload : template))
          .sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(deleteMessageTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter((template) => template.id !== action.payload);
      });
  },
});

export const { resetSendStatus } = announcementsSlice.actions;

export const selectAnnouncements = (state: RootState) => state.announcements.items;
export const selectAnnouncementsListStatus = (state: RootState) => state.announcements.listStatus;
export const selectAnnouncementsPage = (state: RootState) => ({
  page: state.announcements.page,
  totalPages: state.announcements.totalPages,
  totalCount: state.announcements.totalCount,
});
export const selectAnnouncementSendStatus = (state: RootState) => state.announcements.sendStatus;
export const selectAnnouncementSendError = (state: RootState) => state.announcements.sendError;
export const selectMessageTemplates = (state: RootState) => state.announcements.templates;
export const selectMessageTemplatesStatus = (state: RootState) => state.announcements.templatesStatus;

export default announcementsSlice.reducer;
